import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    BulletList,
    Card,
    Exercise,
    LabelBody,
    LabelBodyHeadline,
    LabelDisplay1,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Spacer,
    SwirlyBox,
    Textarea,
} from '../../components';
import {
    IllustrationBulb,
    IllustrationExamineThoughts,
    IllustrationHangingOn,
    Module1S1Exercise2Illustration1,
} from '../../icons';
import { ExerciseStatus, Question } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseViewProps } from '../exercises';

const page2InputKey = 'page2Input';

export const dataKeys = [page2InputKey];
export const questions: Question[] = [
    createQuestionFromConfiguration({
        dataKeys,
        title: 'Hvad observerede du?',
        description:
            'Det kan være godt at skrive det ned, du observerer. På den måde kan du se det lidt udefra.',
    }),
];

export function Module1S1Exercise2({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    return (
        <Exercise title="Biologen">
            <ProgressBar current={exercise.CurrentPage} total={3} />
            <Page />
        </Exercise>
    );

    function Page() {
        const [page2Input, setPage2Input] = useSyncedState(
            exercise.Data[page2InputKey] ?? '',
        );
        switch (exercise.CurrentPage) {
            case 3:
                return (
                    <Page3
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                            });
                        }}
                    />
                );
            case 2:
                return (
                    <Page2
                        page2Input={page2Input}
                        onUpdatePage2Input={setPage2Input}
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 3,
                                data: {
                                    [page2InputKey]: normalizeInput(page2Input),
                                },
                            });
                        }}
                    />
                );
            case 1:
            default:
                return (
                    <Page1
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 2,
                            });
                        }}
                    />
                );
        }
    }
}

type PageProps = {
    onContinue: () => void;
};
function Page1({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">
                Undersøg ubehaget igennem biologens forstørrelsesglas
            </LabelDisplay2>
            <LabelBody>
                Det er et godt redskab at observere og sætte ord på fornemmelser
                og følelser. Det giver mulighed for, at du ikke bliver opslugt,
                af det du mærker. Derfor skal du nu eksperimentere med at
                observere ubehaget.
            </LabelBody>
            <LabelDisplay1 class="mb-10 mt-30">Vælg en situation</LabelDisplay1>
            <LabelBody>
                Du skal starte med at tænke på en situation, hvor du mærkede et
                ubehag. Vælg en situation, der ikke er alt for overvældende.
                F.eks. ubehaget efter et skænderi eller før en eksamen.
            </LabelBody>
            <Module1S1Exercise2Illustration1 className="mx-auto my-20" />
            <LabelBody>
                Det kan være, at du tænker ubehaget vokser, hvis du giver plads
                til det. Ved at observere bruger man tit færre kræfter på det og
                på den måde tager det ikke over, selvom du giver plads til det.
            </LabelBody>
            <SwirlyBox class="my-30">
                Når du har valgt en situation, kan du gå videre.
            </SwirlyBox>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

type Page2Props = PageProps & {
    page2Input: string;
    onUpdatePage2Input: (value: string) => void;
};
function Page2({ page2Input, onUpdatePage2Input, onContinue }: Page2Props) {
    return (
        <>
            <Card>
                <LabelDisplay1>Sådan gør du</LabelDisplay1>
                <LabelBody>
                    Tænk på ubehaget som om du er en biolog, der får øje på
                    noget nyt for første gang.
                </LabelBody>
                <LabelBodyHeadline class="my-10" color="text-dark-red">
                    Du kan for eksempel overveje:
                </LabelBodyHeadline>
                <LabelBody>
                    <BulletList
                        items={[
                            'Hvor sidder den ubehagelige fornemmelse i din krop? F.eks. i brystet, i maven, i hovedet.',
                            'Hvor starter og stopper følelsen?',
                            'Hvilken form har den? F.eks. kantet, blød.',
                            'Bevæger den sig rundt? Og hvordan?',
                            'Er det dybt inde i dig eller er det ved overfladen?',
                        ]}
                    />
                </LabelBody>
                <IllustrationExamineThoughts className="mx-auto mt-10" />
            </Card>
            <LabelDisplay1 class="mb-10 mt-30">
                Giv det lige 5 min...
            </LabelDisplay1>
            <LabelBody>
                Det er en god idé at bruge lidt tid på denne øvelse. Træk
                vejret, mærk efter og prøv at observere ubehaget.
            </LabelBody>
            <IllustrationHangingOn className="mx-auto mt-10" />
            <Card class="my-30">
                <LabelDisplay1>Hvad observerede du?</LabelDisplay1>
                <LabelBody class="mt-10">
                    Det kan være godt at skrive det ned, du observerer. På den
                    måde kan du se det lidt udefra.
                </LabelBody>
                <Textarea text={page2Input} onChangeText={onUpdatePage2Input} />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

function Page3({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Et godt redskab</LabelDisplay2>
            <LabelBody>
                Næste gang du mærker ubehag så prøv at gøre ligesom biologen. Du
                kan observere det og sætte ord på de fornemmelser og følelser du
                får – fremfor at søge at kontrollere dem, ændre dem eller fjerne
                dem. Det kan være en hjælp til at være med ubehaget.
            </LabelBody>
            <IllustrationBulb className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
